import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TypeAttributes } from "rsuite/esm/internals/types";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { ErrorBoundary } from "react-error-boundary";
import { findWhere, difference } from "underscore";
import { Drawer, Popover, Whisper } from "rsuite";
import { Button, Col, Row } from "reactstrap";
import classNames from "classnames";
import pluralize from "pluralize";

import AddViewDrawer, {
  ViewTypesWithField,
} from "../../PageView/AddViewDrawer/AddViewDrawer";
import useUpdateDrawerQueryParams from "../../../hooks/useUpdateDrawerQueryParams";
import {
  Field,
  LayoutAndSections,
  Module,
} from "../../../types/TenantInterfaces";
import ShareScreenDrawer from "../../PageView/ShareScreenDrawer/ShareScreenDrawer";
import SummaryCardWidget from "../../Widgets/SummaryCardWidget/SummaryCardWidget";
import {
  ReusableViewReferenceFields,
  View,
} from "../../../types/PageInterfaces";
import useReorderSubViews from "../../../api/api-hooks/views/useReorderSubViews";
import { ConvoMetadata } from "../../../api/api-hooks/comments/useCreateComment";
import WebsitePreviewDrawer from "../WebsitePreviewDrawer/WebsitePreviewDrawer";
import { GetDefaultLayout, ModuleLayoutSelector } from "./ModuleLayoutSelector";
import BrieRecordAssist from "../../../views/brie/BrieAssist/BrieRecordAssist";
import useTenantUsers from "../../../api/api-hooks/tenantUsers/useTenantUsers";
import ThumbnailWidget from "../../Widgets/ThumbnailWidget/ThumbnailWidget";
import EmailActionsDrawer from "../EmailActionsDrawer/EmailActionsDrawer";
import { recordDetailDrawerOpenCount } from "../../../atoms/GlobalAtoms";
import { RecordSummarySection } from "../../Records/RecordDetailLayout";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import ImageGallery from "../../Records/ImageGallery/ImageGallery";
import RichTextWidget from "../../Widgets/RichText/RichTextWidget";
import RecordDetailWidget from "../../Widgets/RecordDetailWidget";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import useRecord from "../../../api/api-hooks/records/useRecord";
import PageViewRenderer from "../../PageView/PageViewRenderer";
import ManageRecordTags from "../../Records/ManageRecordTags";
import { IsMobileAtom, ScreenSizeAtom } from "../../../App";
import useRecordImage from "../../../hooks/useRecordImage";
import usePage from "../../../api/api-hooks/pages/usePage";
import DotsOptionsMenu from "../../Menus/DotsOptionsMenu";
import useUser from "../../../api/api-hooks/auth/useUser";
import UpdateRecordDrawer from "./UpdateRecordDrawer";
import AppTooltip from "../../../elements/AppTooltip";
import RecordActionsMenu from "./RecordActionsMenu";
import FieldUtils from "../../../utils/FieldUtils";
import AppNavbar from "../../AppNavbar/AppNavbar";
import RecordFeed from "../../Records/RecordFeed";
import useToggle from "../../../hooks/useToggle";

import { useUserRecordActivityUpdate } from "../../Records/References/ReferencesComponents/SelectableReferences/useUpdateUserRecordActivity";
import BasicSharedRecordDrawer from "../ShareRecordDrawer/BasicSharedRecordDrawer";
import useDuplicateView from "../../../api/api-hooks/views/useDuplicateView";
import MultiSubitemTable from "../../Records/Subitems/MultiSubitemTable";
import useDeleteView from "../../../api/api-hooks/views/useDeleteView";
import ShareRecordDrawer from "../ShareRecordDrawer/ShareRecordDrawer";
import { CrashViewComponent } from "../../../views/other/CrashView";
import ViewSettingsDrawer from "../../PageView/ViewSettingsDrawer";
import useCommentParams from "../CommentsDrawer/useCommentParams";
import { Page, PageView } from "../../../types/GlobalInterfaces";
import { insightsOpenAtom } from "../../../views/pages/PageView";
import { EditDashboard } from "../../PageView/PageViewHeader";
import CommentsDrawer from "../CommentsDrawer/CommentsDrawer";
import { editDashboardAtom } from "../../../atoms/ViewAtoms";
import TransformRecordDrawer from "./TransformRecordDrawer";
import AppEmptyState from "../../../elements/AppEmptyState";
import useView from "../../../api/api-hooks/views/useView";
import IconButton from "../../../elements/IconButton";
import Constants from "../../../utils/Constants";
import ShareButton from "./ShareButton";

export const SubitemCardsStateAtom = atom({});

interface Props {
  open: boolean;
  onClose?: () => void;
  recordID: string | undefined;
  module: Module;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  placement?: TypeAttributes.Placement4;
  backdrop?: boolean;
  onExited?: () => void;
  onOpened?: () => void;
  tab?: string;
  defaultRecordLayout?: string;
  view?: View | null;
  /**
   * To know when it was opened from a Reference Field using a Reusable View.
   */
  fromReusableViewReference?: boolean;
  toggleTransformIssueDrawer?: (record: any) => void;
}

const convoMetadata: ConvoMetadata = { RecordName: "", RecordModule: "" };

const ViewRecordDrawer = ({
  open,
  onClose,
  recordID,
  module,
  size = "lg",
  placement = "right",
  backdrop = true,
  onExited,
  onOpened,
  tab = "Summary",
  defaultRecordLayout,
  view,
  fromReusableViewReference,
  toggleTransformIssueDrawer,
}: Props) => {
  let {
    record,
    recordLoading,
    drawerSize,
    toggleDrawerSize,
    setOpenCount,
    activeTab,
    rawTab,
    inheritedViewID,
    setActiveTab,
    handleTabChange,
    currentSubitemField,
    currentRichTextField,
    layout,
    setLayout,
    updateQueryParams,
    resetState,
  } = useViewRecordDetails({
    open,
    recordID,
    module,
    tab,
    defaultRecordLayout,
    viewID: view?.id,
  });
  const { updateUserRecordActivityCache } = useUserRecordActivityUpdate();
  const showInsights = useAtomValue(insightsOpenAtom);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [updateRecordDrawer, setUpdateRecordDrawer] = useState(false);
  const isSharedScreen = window?.location?.pathname?.startsWith("/shared-");

  let { open: commentsOpen, websiteOpen, closeComments } = useCommentParams();
  const [searchParams] = useSearchParams();
  const chatWithFilesOpen = !!searchParams.get("brie");
  commentsOpen = commentsOpen || chatWithFilesOpen;
  const screenSize = useAtomValue(ScreenSizeAtom);
  const useLgScreenComments =
    (screenSize === "lg" || screenSize === "md") &&
    ((commentsOpen && drawerOpened) || websiteOpen);

  useEffect(() => {
    // Added the viewed record  in the 'Recent' section.
    if (!recordID || !module) return;

    updateUserRecordActivityCache({
      userRecordActivities: [
        {
          moduleAPIName: module?.APIName,
          recordIDs: [recordID],
          type: "RecentlyViewed",
        },
      ],
    });
  }, [recordID]);

  return (
    <Drawer
      open={open && !isSharedScreen}
      onOpen={() => {
        setOpenCount((prevCount) => prevCount + 1);

        updateQueryParams({
          module,
          viewID: view?.id,
          recordID,
          action: "push",
        });
      }}
      onEntered={() => {
        callFunctionIfExists(onOpened);
      }}
      onClose={handleClose}
      onExited={() => {
        setOpenCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
        setActiveTab("Summary");

        callFunctionIfExists(onExited);
      }}
      placement={placement}
      size={drawerSize ? "full" : useLgScreenComments ? "lg" : size}
      backdrop={backdrop}
      style={{
        width: drawerSize ? "100%" : "",
        transition: "width 0.05s ease-in",
      }}
      closeButton={false}
      className={
        useLgScreenComments && !drawerSize ? "rs-drawer-xl" : undefined
      }
    >
      <ErrorBoundary
        FallbackComponent={CrashViewComponent}
        resetKeys={[view?.id, activeTab]}
      >
        <Drawer.Body
          className={`pt-0 mb-0 w-100 overflow-hidden`}
          id="rs-drawer-no-pad"
        >
          <div className="w-100 h-100 d-flex">
            <div
              className={`${
                useLgScreenComments ? "w-50" : "w-100"
              } d-inline-block`}
            >
              <Drawer.Header className={`d-block pb-0 mb-3 pt-3`}>
                <ViewRecordHeader
                  record={record}
                  recordID={recordID}
                  recordLoading={recordLoading || !recordID}
                  module={module}
                  drawerSize={drawerSize}
                  toggleDrawerSize={toggleDrawerSize}
                  layout={layout}
                  activeTab={activeTab}
                  rawTab={rawTab}
                  handleTabChange={handleTabChange}
                  onClose={onClose}
                  view={view}
                  drawerOpen={drawerOpened}
                  updateRecordDrawer={updateRecordDrawer}
                  setUpdateRecordDrawer={setUpdateRecordDrawer}
                  inheritedViewID={inheritedViewID}
                  setCommentsDrawerOpen={setDrawerOpened}
                  fromReusableViewReference={fromReusableViewReference}
                  toggleTransformIssueDrawer={toggleTransformIssueDrawer}
                />
              </Drawer.Header>
              <ErrorBoundary
                FallbackComponent={CrashViewComponent}
                resetKeys={[view?.id, activeTab]}
              >
                <div
                  className={`mt--3 pt-2 overflow-auto px-${
                    ["Summary", "History"].includes(activeTab) ? 2 : 4
                  } px-sm-4`}
                  style={{
                    height: "calc(100vh - 135px)",
                    width: "100%",
                  }}
                >
                  <ViewRecordDetails
                    recordLoading={recordLoading || !recordID}
                    record={record}
                    recordID={recordID}
                    layout={layout}
                    module={module}
                    setLayout={setLayout}
                    activeTab={activeTab}
                    currentSubitemField={currentSubitemField}
                    currentRichTextField={currentRichTextField}
                    parentViewID={view?._id as string}
                    drawerSize={drawerSize}
                    showInsights={!!showInsights}
                    setUpdateRecordDrawer={setUpdateRecordDrawer}
                    reusableViewReferenceFields={
                      view?.ReusableViewIDs?.ReferenceFields
                    }
                  />
                </div>
              </ErrorBoundary>
            </div>
            <div
              className={
                useLgScreenComments ? "border-left  d-inline-block w-50" : ""
              }
              style={{
                minWidth: useLgScreenComments || websiteOpen ? 600 : 0,
                height: "calc(100vh - 5px)",
              }}
            >
              <div style={{ height: "calc(100vh - 5px)" }}>
                {!commentsOpen && (
                  <WebsitePreviewDrawer
                    inline={screenSize === "lg" || screenSize === "md"}
                    record={record}
                  />
                )}
                <CommentsDrawer
                  open={commentsOpen && drawerOpened}
                  activeTab={searchParams.get("brie") ? "BrieGPT" : "Comments"}
                  onClose={() => {
                    closeComments();
                    setDrawerOpened(false);
                  }}
                  context="Record"
                  contextID={recordID}
                  convoMetadata={convoMetadata}
                  inline={screenSize === "lg" || screenSize === "md"}
                  recordChat={true}
                  // recordChat={activeTab === "Summary"}
                />
              </div>
            </div>
          </div>
        </Drawer.Body>
      </ErrorBoundary>
    </Drawer>
  );

  function handleClose() {
    updateQueryParams({
      module,
      viewID: view?.id,
      recordID,
      action: "pop",
    });

    setDrawerOpened(false);
    resetState();
    callFunctionIfExists(onClose);
  }
};

export default ViewRecordDrawer;

interface DetailsProps {
  open: boolean;
  onClose?: () => void;
  recordID: string | undefined;
  module?: Module | null;
  tab: string;
  defaultRecordLayout?: string;
  viewID: string | undefined;
}

export function useViewRecordDetails({
  open,
  recordID,
  module,
  tab = "Summary",
  defaultRecordLayout,
  viewID,
}: DetailsProps) {
  const { record, recordLoading } = useRecord({
    moduleAPIName: module?.APIName,
    recordID: recordID,
    enabled: open ? true : false,
  });
  const [drawerSize, toggleDrawerSize] = useToggle();

  const setOpenCount = useSetAtom(recordDetailDrawerOpenCount);
  const [activeTab, setActiveTab] = useState(tab);
  const [inheritedInfo, setInheritedInfo] = useState<{
    viewID: string;
    inheritedViewID: string;
  } | null>(null);
  const [currentSubitemField, setCurrentSubitemField] = useState<any>(null);
  const [currentRichTextField, setCurrentRichTextField] = useState<any>(null);
  const layoutWasIntialized = useRef(false);

  const [layout, setLayout] = useState<null | LayoutAndSections>(
    module && !recordLoading
      ? GetDefaultLayout(module, defaultRecordLayout, record) || null
      : null
  );

  useEffect(() => {
    if (!module || recordLoading || layoutWasIntialized.current === true) {
      return;
    }

    const defaultLayout = GetDefaultLayout(module, defaultRecordLayout, record);
    setLayout(defaultLayout);
  }, [module, defaultRecordLayout, record, recordLoading]);

  const { updateQueryParams } = useUpdateDrawerQueryParams(); // Function that expects (recordID, action, tab)

  useEffect(() => {
    if (tab) handleTabChange(tab);
  }, [tab]);

  const handleSubTab = (val: any) => {
    if (!module || typeof val !== "string") return;

    if (val.startsWith("ReuseView")) {
      val = val.slice(10);
    } else if (val.startsWith("Reuse")) {
      const info = val.split("-");

      setInheritedInfo({
        viewID: info[2],
        inheritedViewID: info[1],
      });

      val = info[2];
    } else {
      setInheritedInfo(null);
    }

    if (!val.startsWith("Subitem") && !val.startsWith("RichText")) {
      setCurrentSubitemField(null);
      setCurrentRichTextField(null);
      return;
    }

    if (val.startsWith("Subitem")) {
      const fieldAPIName = val.split("-", 2)[1];

      const field = findWhere(module?.Fields, { APIName: fieldAPIName });

      setCurrentSubitemField(field);
    }
    if (val.startsWith("RichText")) {
      const fieldAPIName = val.split("-", 2)[1];

      const field = findWhere(module?.Fields, { APIName: fieldAPIName });
      setCurrentRichTextField(field);
    }
  };

  const handleTabChange = (val: any) => {
    if (typeof val !== "string") return;

    setActiveTab(val);
    updateQueryParams({
      module,
      viewID,
      recordID,
      action: "tabchange",
      tab: val as any,
    });

    handleSubTab(val);
  };

  useEffect(() => {
    layoutWasIntialized.current = false;
  }, [module?.APIName]);

  // Setup the Default/Dynamic layout
  // If there is a Dynamic Layout Field, make sure the record finishes loading first!
  useEffect(() => {
    if (!module || layoutWasIntialized.current === true) return;
    if (
      module?.Templates?.RecordDetailView?.DynamicLayoutField &&
      recordLoading
    ) {
      return;
    }

    const matchedLayout = GetDefaultLayout(module, defaultRecordLayout, record);

    if (matchedLayout && matchedLayout?.id !== "Empty") {
      layoutWasIntialized.current = true;
      setLayout(matchedLayout);
    }
  }, [module, defaultRecordLayout, record, recordLoading]);

  const handleSubTabRef = useRef<any>(null);
  handleSubTabRef.current = handleSubTab;

  useEffect(() => {
    if (!module?.Fields) return;

    handleSubTabRef.current(activeTab);
  }, [activeTab, module?.Fields]);

  return {
    record,
    recordLoading: recordLoading || !layout,
    drawerSize,
    toggleDrawerSize,
    setOpenCount,
    activeTab: activeTab?.startsWith("Reuse")
      ? activeTab.startsWith("ReuseView")
        ? activeTab.slice(10)
        : activeTab.split("-")[2] // Removes the "Reuse-123456789012345678901234" start of the string.
      : activeTab,
    rawTab: activeTab,
    setActiveTab,
    inheritedViewID: inheritedInfo?.inheritedViewID,
    handleTabChange,
    currentSubitemField,
    currentRichTextField,
    layout,
    setLayout,
    updateQueryParams,
    resetState: () => {
      layoutWasIntialized.current = false;
    },
  };
}

export function ViewRecordHeader({
  record,
  recordID,
  recordLoading,
  module,
  drawerSize,
  toggleDrawerSize,
  layout,
  rawTab,
  activeTab,
  handleTabChange,
  onClose,
  view,
  updateRecordDrawer,
  setUpdateRecordDrawer,
  inheritedViewID,
  setCommentsDrawerOpen,
  attachmentsField,
  fromReusableViewReference,
  toggleTransformIssueDrawer,
}: {
  record: any;
  recordID: any;
  recordLoading?: boolean;
  module?: Module | null;
  drawerSize?: boolean;
  toggleDrawerSize?: () => any;
  layout: null | LayoutAndSections;
  rawTab: string;
  activeTab: string;
  handleTabChange: (newTab: string) => void;
  onClose?: () => void;
  view?: View | null;
  drawerOpen?: boolean;
  updateRecordDrawer: boolean;
  setUpdateRecordDrawer: Dispatch<SetStateAction<boolean>>;
  inheritedViewID?: string;
  setCommentsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  attachmentsField?: string;
  /**
   * To know when it was opened from a Reference Field using a Reusable View.
   */
  fromReusableViewReference?: boolean;
  toggleTransformIssueDrawer?: (record: any) => void;
}) {
  const [search] = useSearchParams();
  const { pageID, tenantID }: any = useParams();
  const { page }: { page: Page } = usePage(pageID);
  const { duplicateView } = useDuplicateView(pageID);
  const { deleteView } = useDeleteView(pageID);
  const reuseViewID = view?.ReusableViewIDs?.RecordDetail;
  const { view: inheritedView, viewLoading: inheritedViewLoading } = useView({
    pageID,
    viewID: reuseViewID,
    disabled: !reuseViewID,
  });
  const recordName =
    module?.Templates?.RecordListView?.RecordNameField || "_RecordName";
  const emptyName =
    module?.Templates?.RecordListView?.RecordNameEmpty || "(No Name)";
  const subViews = reuseViewID ? inheritedView?.SubViews : view?.SubViews;
  const mobileScreen = useAtomValue(IsMobileAtom);
  const actionsRef = useRef(null);
  const [isEditingOrder, setIsEditingOrder] = useState(false);

  const [editViewTab, setEditViewTab] = useState<PageView | null>(null);
  const [shareViewID, setShareViewID] = useState("");

  const [emailActionsDrawer, toggleEmailActionsDrawer] = useToggle();
  const [dtDrawerOpen, setDtDrawerOpen] = useState(false);
  const [shareRecord, toggleShareRecord] = useToggle();
  const [viewDrawer, setViewDrawer] = useState(false);
  const { openComments } = useCommentParams();

  const { user } = useUser();
  const { cPermissions } = useCloudPermissions();
  const { users } = useTenantUsers(!record?._LockedBy);
  // @ts-ignore
  const { imageField, imageFieldPresentation } = useRecordImage({ module });
  const { reorderTabs } = useReorderSubViews();
  convoMetadata.RecordName = record?.[recordName];
  convoMetadata.RecordModule = module?.APIName || record?._ModuleAPIName;
  const isGlobal = module?.GlobalModule;
  const isSupportCloud = Constants.SUPPORT_CLOUD_IDS.includes(tenantID!);

  const lockedByUser = useMemo(() => {
    if (!users?.length || !record?._LockedBy) return;

    const user = users.find((u: any) => u.id === record?._LockedBy);

    return user;
  }, [users, record?._LockedBy]);

  /**
   * This use memo will validate if the View is a Reused View to disable the option
   * to add a new SubView.
   *
   * ReuseView: Used to create a new View/Subview from an existing one. We only store the
   *             reference of the oiginal View/Subview to reuse it.
   * Reuse: To Reuse a View in a Existing View, to reuse it's Subviews.
   */
  const useDynamicAppNavbar = useMemo(() => {
    // This will help us to know if the Parent View is reusable or not.
    const isReusableViewTab = page?.Views?.some(
      (v) => v.id === view?.id && !!v.ReusableView
    );

    // If the view is a Reuse View (which is different of a ReuseView)
    if (inheritedView || !!fromReusableViewReference) return false;

    // Helps to know a Reusable SubView is being used (open).
    const isReusableView = search.toString().includes(`ReuseView`);

    /**
     * Prevents a bug with the ViewRecordDrawer first SubView layer, where just cliking on a
     * Reusable SubView was hiding the 'Add New Tab' button.
     */
    const searchValues = Array.from(search.values()).flatMap((v) => {
      try {
        return JSON.parse(v);
      } catch (error) {
        return [];
      }
    });

    return (
      !!view &&
      !((isReusableView && searchValues?.length > 1) || isReusableViewTab)
    );
  }, [pageID, view, search]);

  const lockRecordPermissions = () => {
    if (!record) return;
    if (cPermissions.cloud_unlock_records) return true;
    if (record._LockedBy) {
      if (record.CreatedBy.id === user.id || record._LockedBy === user.id) {
        return true;
      }
    } else {
      return true;
    }
    return false;
  };

  const tabs = useMemo(() => {
    const tabs = subViews ? subViews : [];

    if (!tabs?.find((tab) => tab?.Type === "Summary")) {
      tabs.unshift({
        Name: "Summary",
        id: "Summary",
        Type: "Summary",
      });
    }
    if (!tabs?.find((tab) => tab?.Type === "History")) {
      tabs.push({
        Name: "History",
        id: "History",
        Type: "History",
      });
    }

    return tabs?.map((tab: PageView) => {
      const hasView = !!view;
      let removeable = !["Summary", "History"].includes(tab?.Type);
      const hasSettings = ![
        "Summary",
        "History",
        ...ViewTypesWithField,
        "External",
      ].includes(tab?.Type);

      if (!["Summary", "History"].includes(tab?.Type)) {
        removeable = useDynamicAppNavbar;
      }

      // Rename the Summary tab to "Details" for better UX
      const tabName = tab?.Type === "Summary" ? "Details" : tab?.Name;

      const title = (
        <>
          {tab?.Type === "External" && (
            <i
              className="mdi mdi-open-in-new pr-2"
              style={{ position: "relative", top: 1, fontSize: 15 }}
            />
          )}
          {Array.isArray(tab?.Shared) && tab?.Shared.includes(recordID) && (
            <i
              className="mdi mdi-share-variant text-primary pr-2"
              style={{ position: "relative", top: 1, fontSize: 15 }}
            />
          )}
          <div className="d-inline-block">{tabName}</div>
          {hasView && (
            <DotsOptionsMenu
              options={
                <PopoverDropdownMenu>
                  <DropdownOptionWithIcon
                    icon="mdi mdi-reorder-vertical"
                    onClick={() => setIsEditingOrder(true)}
                    name="Reorder Tabs"
                    disabled={!useDynamicAppNavbar}
                  />
                  {hasSettings && (
                    <DropdownOptionWithIcon
                      icon="mdi mdi-share-variant"
                      onClick={() => setShareViewID(tab?.id)}
                      name="Share View"
                      disabled={!useDynamicAppNavbar}
                    />
                  )}
                  {hasSettings && (
                    <DropdownOptionWithIcon
                      icon="mdi mdi-cog"
                      onClick={() => setEditViewTab(tab)}
                      name="View Settings"
                    />
                  )}
                  {hasSettings && (
                    <DropdownOptionWithIcon
                      name="Duplicate View"
                      icon="mdi mdi-content-copy"
                      onClick={() =>
                        duplicateView({
                          viewID: tab?.id,
                          parentViewID: view?._id,
                          onSuccess: (newViewID: string) =>
                            handleTabChange(newViewID),
                        })
                      }
                      disabled={!useDynamicAppNavbar}
                    />
                  )}
                  <DropdownOptionWithIcon
                    name="Delete View"
                    icon="mdi mdi-delete"
                    disabled={!removeable}
                    onClick={() =>
                      deleteView({
                        viewID: tab?.id,
                        parentViewID: view?._id,
                        onSuccess: () => handleTabChange("Summary"),
                      })
                    }
                  />
                </PopoverDropdownMenu>
              }
            />
          )}
        </>
      );

      let activeValue = tab?.id;

      if (tab.Type === "RichText") {
        activeValue = `RichText-${tab?.FieldAPIName}-${activeValue}`;
      }
      if (tab.Type === "Gallery") {
        activeValue = `Gallery-${tab?.FieldAPIName}-${activeValue}`;
      }
      if (tab.Type === "Subitem") {
        activeValue = `Subitem-${tab?.FieldAPIName}-${activeValue}`;
      }
      if (reuseViewID || inheritedViewID) {
        activeValue = `Reuse-${reuseViewID || inheritedViewID}-${activeValue}`;
      }

      if (tab.ReusableView) {
        activeValue = `ReuseView-${activeValue}`;
      }

      return {
        title: title,
        activeValue: activeValue,
        disableReorder: false,
        externalLink: tab.Type === "External" ? tab.Link : undefined,
      };
    });
  }, [subViews, reuseViewID, inheritedViewID, useDynamicAppNavbar]);

  const actionsDisabled = recordLoading || !record;
  const [openShareRecordDrawer, toggleShareRecordDrawer] = useToggle();

  return (
    <>
      <Row className="align-items-center">
        <Col
          xs=""
          style={{ maxWidth: "calc(100% - 70px)" }}
          className="pr-0 text-truncate"
        >
          {recordLoading ? (
            <div
              className="skeleton mb-2 mt-1"
              style={{ width: 80, height: 20 }}
            ></div>
          ) : (
            <div
              style={{ fontSize: 14 }}
              className="tw:font-medium tw:flex tw:items-center tw:gap-1"
            >
              {module?.Name && pluralize.singular(module?.Name)}
              {!!record?._TenantUser && (
                <img
                  className="ml-1"
                  src="/img/GCLogo512x512.png"
                  style={{
                    height: 22,
                    width: 22,
                    marginTop: -1,
                  }}
                />
              )}
              {!!record?._Synced && !record?._TenantUser && (
                <>
                  <IconButton
                    icon="cloud-sync-outline"
                    color="secondary"
                    className="tw:text-info"
                    outline={true}
                    size="sm"
                    onClick={toggleShareRecordDrawer}
                    style={{ height: 28, width: 28 }}
                  />
                  <BasicSharedRecordDrawer
                    open={openShareRecordDrawer}
                    onClose={toggleShareRecordDrawer}
                    recordID={record?.id}
                    moduleAPIName={record?._ModuleAPIName}
                  />
                </>
              )}
            </div>
          )}
          <Drawer.Title style={{ fontWeight: 600, fontSize: 17 }}>
            {recordLoading ? (
              <div
                className="skeleton mb-2"
                style={{ width: 300, height: 18 }}
              ></div>
            ) : record ? (
              <div className="d-flex align-items-center">
                {!!imageField && (
                  <ThumbnailWidget
                    record={record}
                    imageKey={imageField}
                    mode={imageFieldPresentation || undefined}
                    size="md"
                    className="tw:mb-1 tw:font-normal tw:text-gray-700"
                  />
                )}
                <div className="wrap-2">
                  {record?.[recordName] || emptyName}
                </div>
                {record._LockedBy && (
                  // @ts-ignore
                  <AppTooltip
                    useTooltip
                    placement={"autoHorizontalEnd"}
                    content={
                      user
                        ? `Record locked by ${lockedByUser?.Name ?? "Unknown"}`
                        : "Record is locked."
                    }
                  >
                    <i
                      className="ml-3 mdi mdi-lock-outline"
                      style={{ fontSize: 22 }}
                    ></i>
                  </AppTooltip>
                )}
              </div>
            ) : (
              <div className="text-danger h4">Record Not Found</div>
            )}
          </Drawer.Title>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          {!(isGlobal && !isSupportCloud) && (
            <Button
              className="btn-icon-only"
              color="secondary"
              outline
              onClick={() => {
                openComments(attachmentsField ? "BrieGPT" : undefined);
                setCommentsDrawerOpen(true);
              }}
              size="sm"
              disabled={actionsDisabled}
            >
              <i className="mdi mdi-comment-outline" />
            </Button>
          )}

          {!mobileScreen && (
            <Button
              className="btn-icon-only"
              color="secondary"
              outline
              onClick={toggleShareRecord}
              size="sm"
              disabled={actionsDisabled}
            >
              <i className="mdi mdi-share-variant" />
            </Button>
          )}
          {module && activeTab === "Summary" && !actionsDisabled && (
            <BrieRecordAssist module={module} record={record} />
          )}

          {!actionsDisabled && (
            <EditDashboardButton activeTab={activeTab} pageID={pageID} />
          )}
          {!mobileScreen && toggleDrawerSize && (
            <Button
              className="btn-icon-only"
              color="secondary"
              outline
              onClick={toggleDrawerSize}
            >
              <i
                className={
                  drawerSize ? "mdi mdi-fullscreen-exit" : "mdi mdi-fullscreen"
                }
              />
            </Button>
          )}
          <Whisper
            preventOverflow
            placement="autoVerticalEnd"
            trigger="click"
            ref={actionsRef}
            speaker={
              <Popover
                className="sidebar-popover"
                full
                onClick={(e: any) => e.stopPropagation()}
              >
                {module && (
                  <RecordActionsMenu
                    record={record}
                    toggleUpdateRecordDrawer={() => setUpdateRecordDrawer(true)}
                    toggleEmailActionsDrawer={toggleEmailActionsDrawer}
                    toggleShareRecord={toggleShareRecord}
                    toggleDtDrawer={() => {
                      if (
                        module?.APIName === "Feedback" &&
                        toggleTransformIssueDrawer
                      ) {
                        toggleTransformIssueDrawer(record);
                      } else {
                        setDtDrawerOpen(true);
                      }
                    }}
                    actionsRef={actionsRef}
                    module={module}
                    onClose={() => callFunctionIfExists(onClose)}
                    pageID={pageID}
                    viewID={view?.id}
                    tab={activeTab}
                  />
                )}
              </Popover>
            }
            controlId="newRecordDropdown"
          >
            <Button color="secondary" outline className="btn-icon-only">
              <i className="mdi mdi-dots-horizontal cursor-pointer" />
            </Button>
          </Whisper>
        </Col>
      </Row>

      <div className="mt--1">
        {recordLoading ? (
          <div
            className="skeleton mr-3"
            style={{ width: 125, height: 20 }}
          ></div>
        ) : (
          record && (
            // @ts-ignore
            <ManageRecordTags
              moduleAPIName={module?.APIName}
              recordId={record?.id}
              recordTagIds={record?.Tags}
              modifyTrigger={"click"}
              display="ViewRecordDrawer"
              disabled={!lockRecordPermissions() || !record || recordLoading}
            />
          )
        )}
      </div>
      <div className="mt-1 ml-sm--4 ml--5">
        {recordLoading || (reuseViewID && inheritedViewLoading) ? (
          <div className="d-flex mt-3 mb-2">
            <div
              className="skeleton mr-3"
              style={{ width: 125, height: 20 }}
            ></div>
            <div
              className="skeleton mr-3"
              style={{ width: 125, height: 20 }}
            ></div>
            <div className="skeleton" style={{ width: 125, height: 20 }}></div>
          </div>
        ) : (
          record && (
            <AppNavbar
              tabs={tabs}
              activeTab={rawTab}
              onSelect={handleTabChange}
              dynamic={useDynamicAppNavbar}
              dynamicTitle="Tab"
              isEditingOrder={isEditingOrder}
              setIsEditingOrder={setIsEditingOrder}
              showDisabled={false}
              onReorder={(newOrder) => {
                const order = newOrder.map((id: string) => {
                  const splitID = id?.split("-");
                  if (splitID.length === 3) id = splitID[2];
                  return id;
                });

                reorderTabs({
                  pageID,
                  parentViewID: view?._id as string,
                  newOrder: order,
                });
              }}
              onAdd={() => setViewDrawer(true)}
            />
          )
        )}
      </div>
      <UpdateRecordDrawer
        open={updateRecordDrawer}
        onClose={() => setUpdateRecordDrawer(false)}
        recordID={recordID}
        moduleAPIName={module?.APIName}
        layout={layout}
      />
      <TransformRecordDrawer
        open={dtDrawerOpen}
        onClose={() => setDtDrawerOpen(false)}
        sourceRecordID={recordID}
        module={module}
      />
      <AddViewDrawer
        open={viewDrawer}
        handleClose={() => setViewDrawer(false)}
        parentViewID={view?._id}
        onAdd={(newViewID?: string) => {
          newViewID && handleTabChange(newViewID);
        }}
        subViews={subViews}
        globalAppID={view?.GlobalAppID}
        isSubView={true}
        recordModule={module || undefined}
      />
      <ViewSettingsDrawer
        viewID={editViewTab?.id}
        isReusedView={
          !!reuseViewID ||
          !!inheritedViewID ||
          !!editViewTab?.ReusableView ||
          fromReusableViewReference
        }
        onClose={() => setEditViewTab(null)}
        pageID={pageID}
        parentViewID={view?._id}
      />
      <ShareScreenDrawer
        open={!!shareViewID}
        onClose={() => setShareViewID("")}
        viewID={shareViewID}
        recordID={recordID}
        recordModule={module?.APIName}
        parentViewID={view?._id}
        pageID={pageID}
        documentID={undefined}
      />
      {cPermissions.cloud_update_workflow && (
        <EmailActionsDrawer
          open={emailActionsDrawer}
          onClose={toggleEmailActionsDrawer}
          recordID={recordID}
        />
      )}
      <ShareRecordDrawer
        open={shareRecord}
        onClose={toggleShareRecord}
        recordModule={module?.APIName as string}
        globalAppID={view?.GlobalAppID}
        record={record}
      />
    </>
  );
}

export function ViewRecordDetails({
  recordLoading,
  record,
  recordID,
  layout,
  module,
  setLayout,
  activeTab,
  currentRichTextField,
  parentViewID,
  drawerSize,
  page,
  view,
  showInsights,
  setUpdateRecordDrawer,
  reusableViewReferenceFields,
}: {
  module?: Module | null;
  record: any;
  recordID: string | undefined;
  recordLoading?: boolean;
  layout: null | LayoutAndSections;
  setLayout: Dispatch<SetStateAction<LayoutAndSections | null>>;
  activeTab: string;
  currentSubitemField: any;
  currentRichTextField: any;
  parentViewID?: string;
  drawerSize?: boolean;
  view?: View;
  page?: Page;
  showInsights?: boolean;
  setUpdateRecordDrawer: Dispatch<SetStateAction<boolean>>;
  /**
   * Optional parameter to show a View-SubView data when clicking on a Rereference Record.
   * Needs to be from the original View, nor the Reusable One.
   *
   * For 'Summary' Tab.
   */
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}) {
  const screenSize = useAtomValue(ScreenSizeAtom);

  if (recordLoading || !module) {
    return (
      <div className="mx-2">
        <div
          className="skeleton mb-2 mt-2"
          style={{ width: "100%", height: 400 }}
        ></div>
      </div>
    );
  }

  if (!recordLoading && !record) {
    return (
      <AppEmptyState
        hideIcon
        title="Record Not Found"
        description="Something went wrong when fetching this record."
      />
    );
  }

  if (!activeTab) return null;

  if (activeTab === "Summary") {
    return (
      <RecordSummaryTab
        module={module}
        record={record}
        layout={layout}
        setLayout={setLayout}
        screenSize={screenSize}
        setUpdateRecordDrawer={setUpdateRecordDrawer}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (activeTab === "History") return <RecordFeed recordID={record?.id} />;

  if (activeTab.startsWith("Subitem")) {
    const fieldAPIName = activeTab.split("-", 2)[1];
    const fieldSchema = module.Fields.find(
      (field: Field) => field.APIName === fieldAPIName
    );
    return (
      <MultiSubitemTable
        records={record[fieldAPIName]}
        fieldSchema={fieldSchema}
        parentRecord={record}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (activeTab.startsWith("RichText")) {
    return (
      <RichTextWidget
        key={currentRichTextField?.APIName}
        recordID={recordID}
        field={currentRichTextField}
        savedRichText={record?.[currentRichTextField?.APIName]?.RichText}
        moduleAPIName={module?.APIName}
        title={currentRichTextField?.Name}
        description={currentRichTextField?.Description}
      />
    );
  }

  if (activeTab.startsWith("Gallery")) {
    const galleryAPIName = activeTab.split("-", 2)[1];

    return (
      <ImageGallery
        recordID={recordID}
        field={
          module?.Fields?.find((f) => f.APIName === galleryAPIName) as Field
        }
        moduleAPIName={module?.APIName}
      />
    );
  }

  if (!parentViewID) return null;

  // This if statement adds support for Shared Record Pages.
  if (view && page) {
    return (
      <SubViewRendererWithData
        parentViewID={parentViewID}
        record={record}
        drawerSize={drawerSize}
        page={page}
        view={view}
      />
    );
  }

  return (
    <SubViewRenderer
      viewID={activeTab}
      parentViewID={parentViewID}
      record={record}
      drawerSize={drawerSize}
      showInsights={showInsights}
    />
  );
}

export function RecordSummaryTab({
  module,
  record,
  layout,
  setLayout,
  screenSize,
  setUpdateRecordDrawer,
  reusableViewReferenceFields,
}: {
  module?: Module | null;
  record: any;
  layout: null | LayoutAndSections;
  setLayout: Dispatch<SetStateAction<LayoutAndSections | null>>;
  screenSize: string | null;
  setUpdateRecordDrawer: Dispatch<SetStateAction<boolean>> | undefined;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}) {
  const formatRecordDetails = (module: any, record: any) => {
    if (!module || !record) return null;

    const sections = [];
    const matchedFields = [];
    const allFields = [];
    const subitemFields = []; // Any fields of type "Subitem" or "MultiSubitem"

    module.RecordID = record.id;
    module.Record = record;

    let fieldsToCheck = module.Fields;

    if (!layout?.Default) {
      fieldsToCheck =
        layout?.Sections?.flatMap((s) =>
          s.Type === "RecordSummarySection"
            ? s.Fields.flatMap((f) => {
                const matchedField = module?.Fields?.find(
                  (f2: any) => f.Field === f2.APIName
                );

                return matchedField ? [matchedField] : [];
              })
            : []
        ) ?? [];
    }

    // Displaying all the fields when the layouts sections are empty.
    if (!fieldsToCheck?.length && !layout?.Sections?.length) {
      fieldsToCheck = module.Fields;
    }

    for (let i = 0; i < fieldsToCheck.length; i++) {
      const field = fieldsToCheck[i];

      if (!field) continue;

      const fieldType = field.FieldType;

      if (FieldUtils.fieldTypeIsSubitem(fieldType)) {
        subitemFields.push(field);
        continue;
      }

      allFields.push(field?.APIName);
    }

    const firstSectionOptions = (
      <>
        {Array.isArray(layout?.ShareOptions) &&
          layout?.ShareOptions?.length > 0 && (
            <ShareButton
              module={module}
              record={record}
              shareOptions={layout.ShareOptions!}
            />
          )}
        <ModuleLayoutSelector
          module={module}
          layout={layout}
          setLayout={setLayout}
          style={{ width: 150 }}
          hideOnOne
          className="my-0 ml-1 mr-1"
        />
        {(screenSize === "xs" || screenSize === "sm") &&
          setUpdateRecordDrawer &&
          layout?.Sections?.[0]?.Type !== "RichTextSection" && (
            <IconButton
              size="sm"
              icon="pencil"
              onClick={() => setUpdateRecordDrawer(true)}
            />
          )}
      </>
    );

    const layoutSections = layout?.Sections ?? [];

    if (layoutSections?.length) {
      for (let i = 0; i < layoutSections?.length; i++) {
        const section = layoutSections[i];
        let headerOptions;

        if (module && i === 0) {
          headerOptions = firstSectionOptions;
        }

        if (section.Type === "StageProgress") {
          sections.push(
            <RecordDetailWidget
              module={module}
              record={record}
              section={section}
              detailViewLayoutID={layout?.id ?? ""}
              headerOptions={headerOptions}
            />
          );
        } else if (section.Type === "RichTextSection") {
          sections.push(
            <RecordDetailWidget
              module={module}
              record={record}
              section={section}
              detailViewLayoutID={layout?.id ?? ""}
              headerOptions={headerOptions}
            />
          );

          matchedFields.push(section.Field);
        } else if (section?.Fields?.length) {
          // This else if is mainly here for: section.Type === "RecordSummarySection"
          // But covers all other Types not listed in the if statements above.
          sections.push(
            <RecordDetailWidget
              module={module}
              record={record}
              section={section}
              detailViewLayoutID={layout?.id ?? ""}
              headerOptions={headerOptions}
              reusableViewReferenceFields={reusableViewReferenceFields}
            />
          );

          for (let j = 0; j < section.Fields.length; j++) {
            matchedFields.push(section.Fields[j].Field);
          }
        }
      }
    }

    /**
     * Displaying 'Additional Information' when the layout is set to Default
     * or when there are no Sections in the layout (to not display an empty drawer).
     */
    if (layout?.Default === true || !layout?.Sections?.length) {
      const additionalSection: RecordSummarySection = {
        id: "AdditionalInformation",
        Title: "Additional Information",
        Fields: [],
        Type: "RecordSummarySection",
      };

      const missingFields = difference(allFields, matchedFields);

      for (let i = 0; i < missingFields.length; i++) {
        if (missingFields[i] === "_RecordName" || missingFields[i] === "Tags") {
          continue;
        }

        additionalSection.Fields.push({
          Field: missingFields[i],
          ColSpan: 1,
        });
      }

      sections.push(
        <RecordDetailWidget
          module={module}
          record={record}
          section={additionalSection}
          detailViewLayoutID={layout?.id ?? ""}
          headerOptions={
            !layoutSections?.length ? firstSectionOptions : undefined
          }
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
      );
    }

    if (subitemFields.length < 1) return sections;

    // Handle Subitems Fields - Currently placing them at the bottoom of CreateRecordRendererV2
    for (let i = 0; i < subitemFields.length; i++) {
      const field = subitemFields[i];

      // Skipping the subitem fields that were already added into a section.
      if (
        !!matchedFields.find(
          (f) => f === field.APIName || (f as any)?.APIName === field.APIName
        )
      ) {
        continue;
      }
      // If SubitemDisplayMode is true, Filter Subitem out - It will be displayed as its own tab.
      // if (!!field.SubitemDisplayMode) continue;

      sections.push(
        <SummaryCardWidget
          records={
            Array.isArray(record?.[field?.APIName])
              ? record?.[field?.APIName]
              : !!record?.[field?.APIName]
              ? [record?.[field?.APIName]]
              : []
          }
          module={module}
          fieldSchema={field}
          parentRecord={record}
          readOnly={false}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
      );
    }

    return sections;
  };

  const recordDetailSection = useMemo(
    () => formatRecordDetails(module, record),
    [module, record, layout]
  );

  return (
    <Col xs={12} className="pb-6">
      {recordDetailSection && recordDetailSection?.length > 0 && (
        <>
          {recordDetailSection.map((section: any, i: number) => {
            const isFirstSection = i === 0;

            return (
              <div
                key={i}
                className={classNames("position-relative", {
                  "mt-3": isFirstSection && screenSize === "xs",
                })}
              >
                {section}
              </div>
            );
          })}
        </>
      )}
    </Col>
  );
}

function SubViewRendererWithData({
  parentViewID,
  view,
  page,
  record,
  drawerSize,
}: {
  view: View;
  page: Page;
  record: any;
  drawerSize?: boolean;
  parentViewID: string;
}) {
  const setEditMode = useSetAtom(editDashboardAtom);

  useEffect(() => {
    return () => setEditMode(false);
  }, []);

  return (
    <div className={classNames({ "mx--4": !drawerSize }, "pb-4")}>
      <PageViewRenderer
        key={view?.id ?? ""}
        page={page}
        pageLoading={false}
        view={view || null}
        viewLoading={false}
        viewID={view?.id ?? ""}
        parentViewID={parentViewID}
        record={record}
      />
    </div>
  );
}

function SubViewRenderer({
  parentViewID,
  viewID,
  record,
  drawerSize,
  showInsights,
}: {
  parentViewID: string;
  viewID: string;
  record: any;
  drawerSize?: boolean;
  showInsights?: boolean;
}) {
  const { pageID }: any = useParams();
  const { page, pageLoading } = usePage(pageID);
  const { view, viewLoading } = useView({ pageID, viewID });
  const setEditMode = useSetAtom(editDashboardAtom);

  useEffect(() => {
    return () => setEditMode(false);
  }, []);

  return (
    <div className={classNames({ "mx--4": !drawerSize }, "pb-4")}>
      <PageViewRenderer
        key={viewID}
        page={page}
        pageLoading={pageLoading}
        view={view || null}
        viewLoading={viewLoading}
        viewID={viewID}
        parentViewID={parentViewID}
        record={record}
        showInsights={showInsights}
        reusableViews={view?.ReusableViewIDs}
      />
    </div>
  );
}

function EditDashboardButton({
  activeTab,
  pageID,
}: {
  pageID: string;
  activeTab: string;
}) {
  const noView =
    !activeTab ||
    activeTab === "Summary" ||
    activeTab === "History" ||
    activeTab?.startsWith("Gallery") ||
    activeTab?.startsWith("Subitem") ||
    activeTab?.startsWith("RichText");

  const { view } = useView({ viewID: activeTab, pageID, disabled: noView });

  if (noView) return null;

  if (!view) return null;
  if (!view?.Insights && view?.Type !== "Dashboard") return null;

  return <EditDashboard view={view} />;
}
